// Client helpers for the targeted per-application notifications feature. Backed
// by t1/v1/notifications (cres-aws-t5 handlers/cres/v1/notifications.py):
//   GET  -> notifications applicable to the current user (+ per-user read state)
//   PUT  -> mark one ({USID}) or all ({All:true}) read
// The bell (NavBar) renders these; each notification's `notificationType` tells
// the UI what to do on click (see notificationTarget()).


// The suite application this front-end represents. Sent to the notifications
// endpoint so the bell shows notifications intended for this app or for all apps.
export const CURRENT_APPLICATION = "QMS";

export type AppNotification = {
    usid: string;
    /** Apps this notification is intended for (subset of QMS/Developer/
     *  SBOM/DataCatalog); null means ALL applications. */
    applications: string[] | null;
    /** App-specific type the UI dispatches on (what to do when clicked). */
    notificationType: string;
    title: string;
    body: string;
    /** Parsed action-context JSON (may carry a `url` and type-specific fields). */
    data: any;
    /** Convenience deep link (data.url), when present. */
    url: string;
    read: boolean;
    createdDatetime: string;
};

export type NotificationsResult = {
    notifications: AppNotification[];
    unreadCount: number;
};

function toNotification(raw: any): AppNotification {
    const data = raw?.Data ?? null;
    const url = data && typeof data === "object" && data.url ? String(data.url) : "";
    return {
        usid: raw?.USID ?? "",
        applications: Array.isArray(raw?.Applications) ? raw.Applications : null,
        notificationType: raw?.NotificationType ?? "",
        title: raw?.Title ?? "",
        body: raw?.Body ?? "",
        data,
        url,
        read: !!raw?.Read,
        createdDatetime: raw?.CreatedDatetime ?? "",
    };
}

/** Notifications for the current user, newest first, plus the unread count.
 *  Scoped to this app (and all-app notifications) unless `application` is "". */
export async function fetchNotifications(api: any, application: string = CURRENT_APPLICATION): Promise<NotificationsResult> {
    const res = await api({
        method: "GET",
        url: "t1/v1/notifications",
        params: application ? {Application: application} : undefined,
    });
    const body = res?.data ?? {};
    const list = Array.isArray(body.Notifications) ? body.Notifications : [];
    return {
        notifications: list.map(toNotification),
        unreadCount: Number(body.UnreadCount) || 0,
    };
}

/** Mark a single notification read for the current user. */
export async function markNotificationRead(api: any, usid: string): Promise<void> {
    if (!usid) return;
    await api({method: "PUT", url: "t1/v1/notifications", data: {USID: usid}});
}

/** Mark every applicable notification read for the current user. */
export async function markAllNotificationsRead(api: any): Promise<void> {
    await api({method: "PUT", url: "t1/v1/notifications", data: {All: true}});
}

/**
 * Where clicking a notification should take the user, dispatched by
 * notificationType (falling back to a `url` in its data). Returning "" means no
 * navigation. Extend this as new notification types are added.
 */
export function notificationTarget(n: AppNotification): string {
    // Every Foundation notification carries its own deep link in Data.url -- the approval service
    // builds it from _ROUTES, which a test holds to covering every governed type. The switch stays
    // because the next notification kind may need to compute one; today none does.
    switch (n.notificationType) {
        default:
            return n.url || "";
    }
}


/** Immediate, a daily digest, or none at all. */
export type EmailMode = "IMMEDIATE" | "DAILY" | "OFF";

export const EMAIL_MODES: {value: EmailMode; label: string; detail: string}[] = [
    {value: "IMMEDIATE", label: "As they happen",
     detail: "One email per notification, sent within a minute."},
    {value: "DAILY", label: "Once a day",
     detail: "Everything from the day, collapsed into a single message."},
    {value: "OFF", label: "Never",
     detail: "Notifications still arrive here; nothing is emailed."},
];

/** How much notification email this person gets. IMMEDIATE when they have never said. */
export async function fetchEmailPreference(api: any): Promise<EmailMode> {
    const res = await api({method: "GET", url: "t1/v1/notification_email_preference"});
    return (res?.data?.Mode as EmailMode) || "IMMEDIATE";
}

export async function setEmailPreference(api: any, mode: EmailMode): Promise<EmailMode> {
    const res = await api({
        method: "PUT", url: "t1/v1/notification_email_preference", data: {Mode: mode},
    });
    return (res?.data?.Mode as EmailMode) || mode;
}
